/*
 * ohli-frete-pdp.js — Calculadora de frete + prazo na PDP.
 *
 * Cria um carrinho DESCARTÁVEL via Storefront API (isolado do carrinho real da
 * cliente), resolve o CEP em UF/cidade via ViaCEP, atribui como endereço e lê as
 * opções de frete do carrier service (Olist) — idênticas às do checkout.
 *
 * Detalhes de schema (Storefront API 2026-07):
 *  - o carrier service só é consultado com deliveryGroups(withCarrierRates: true),
 *    que EXIGE @defer -> a cotação chega num 2º chunk do stream multipart/mixed.
 *  - o carrier exige provinceCode (só o zip retorna 0 opções) -> daí o ViaCEP.
 *  - preço/opções continuam autoritativos na versão estável. Em paralelo, a
 *    `unstable` enriquece cada opção com deliveryPromise (ex.: "5 dias úteis").
 *    Se o campo experimental falhar ou mudar, a cotação estável segue intacta.
 *  - qtd é sempre 1 (a PDP da Ohli não tem seletor de quantidade).
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 8000; // teto por requisição avulsa E deadline global do cálculo

  var Q_CREATE =
    'mutation($lines:[CartLineInput!]!){cartCreate(input:{lines:$lines,buyerIdentity:{countryCode:BR},attributes:[{key:"_ohli_frete_pdp",value:"1"}]}){cart{id}userErrors{message}}}';
  var Q_ADDR =
    'mutation($id:ID!,$a:CartSelectableAddressInput!){cartDeliveryAddressesAdd(cartId:$id,addresses:[$a]){userErrors{message}}}';
  var Q_RATES =
    'query($id:ID!){cart(id:$id){id ...F @defer}} fragment F on Cart{deliveryGroups(first:10,withCarrierRates:true){nodes{id deliveryOptions{handle code title estimatedCost{amount currencyCode}}}}}';
  var Q_RATES_PROMISE =
    'query($id:ID!){cart(id:$id){id ...F @defer}} fragment F on Cart{deliveryGroups(first:10,withCarrierRates:true){nodes{id deliveryOptions{handle code title deliveryPromise estimatedCost{amount currencyCode}}}}}';

  var RETRY_MSG = 'Não foi possível calcular o frete agora. Tente novamente.';

  // Escapa dados de terceiros (ViaCEP, Storefront) antes de irem pro innerHTML.
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // fetch com timeout (AbortController). Se um `signal` externo for passado (deadline
  // global do calcular), ele governa e não criamos timer próprio. Senão, um timer de
  // TIMEOUT_MS aborta a request; o timer só é limpo quando o CORPO é lido (text/json),
  // cobrindo o stream multipart/@defer da cotação (2º chunk chega depois dos headers).
  function fetchTimeout(url, opts) {
    opts = Object.assign({}, opts); // não muta o objeto do chamador
    if (opts.signal || typeof AbortController === 'undefined') {
      return fetch(url, opts);
    }
    var ctrl = new AbortController();
    var t = setTimeout(function () {
      ctrl.abort();
    }, TIMEOUT_MS);
    opts.signal = ctrl.signal;
    return fetch(url, opts).then(
      function (r) {
        ['text', 'json'].forEach(function (m) {
          var orig = r[m].bind(r);
          r[m] = function () {
            return orig().finally(function () {
              clearTimeout(t);
            });
          };
        });
        return r;
      },
      function (e) {
        clearTimeout(t);
        throw e;
      }
    );
  }

  function maskCEP(v) {
    var d = (v || '').replace(/\D/g, '').slice(0, 8);
    return d.length > 5 ? d.slice(0, 5) + '-' + d.slice(5) : d;
  }

  // "Olist Envios J&T Express" -> "J&T Express" (vitrine limpa; a transportadora é o que importa)
  function cleanTitle(t) {
    var name = (t || 'Frete').replace(/^Olist\s+Envios\s+/i, '').trim() || 'Frete';
    // "Pax" sozinho é genérico -> mantém a marca "Olist Pax" p/ clareza
    if (/^pax$/i.test(name)) name = 'Olist ' + name;
    return name;
  }

  function fmtBRL(amount) {
    var n = parseFloat(amount);
    if (isNaN(n)) return '—';
    if (n === 0) return 'Grátis';
    try {
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    } catch (e) {
      return 'R$ ' + n.toFixed(2).replace('.', ',');
    }
  }

  function gql(root, body, accept, signal, apiVersion) {
    var url =
      'https://' +
      root.dataset.shopDomain +
      '/api/' +
      (apiVersion || root.dataset.apiVersion) +
      '/graphql.json';
    var headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': root.dataset.token,
    };
    if (accept) headers.Accept = accept;
    var opts = { method: 'POST', headers: headers, body: JSON.stringify(body) };
    if (signal) opts.signal = signal;
    return fetchTimeout(url, opts);
  }

  // Lê o variant selecionado ao vivo no <form> de add-to-cart (resiste à troca de variante).
  function currentVariantGID(root) {
    var form = root.dataset.formId && document.getElementById(root.dataset.formId);
    var input = form && form.querySelector('[name="id"]');
    var raw = (input && input.value) || root.dataset.variantId;
    return 'gid://shopify/ProductVariant/' + raw;
  }

  // A PDP da Ohli não tem seletor de quantidade -> sempre cota 1 unidade.

  // Variante selecionada está disponível? O Dawn desabilita o botão de compra
  // ([name="add"]) quando a variante está esgotada -> usamos isso como sinal.
  function variantAvailable(root) {
    var form = root.dataset.formId && document.getElementById(root.dataset.formId);
    var addBtn = form && form.querySelector('[name="add"]');
    return !(addBtn && addBtn.disabled);
  }

  // Cria (ou reusa) o carrinho descartável para a variante atual (qtd sempre 1).
  // Disparado no 1º input do CEP (intenção real) -> quando a cliente vai calcular,
  // o cartCreate (~500ms) já rodou em paralelo. Reusa só se a variante bater.
  function ensureCart(root) {
    var variantId = currentVariantGID(root);
    var cached = root._ohliCart;
    if (cached && cached.variantId === variantId) {
      return cached.promise;
    }
    var promise = gql(root, {
      query: Q_CREATE,
      variables: { lines: [{ merchandiseId: variantId, quantity: 1 }] },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        var cc = res && res.data && res.data.cartCreate;
        var id = cc && cc.cart && cc.cart.id;
        if (!id) throw new Error('cartCreate');
        return id;
      });
    // se falhar, limpa o cache p/ a próxima chamada recriar
    promise.catch(function () {
      if (root._ohliCart && root._ohliCart.promise === promise) root._ohliCart = null;
    });
    root._ohliCart = { variantId: variantId, promise: promise };
    return promise;
  }

  // Extrai os deliveryOptions dos chunks do stream multipart/mixed.
  // Retorna { opts, hadError, gotDeferred }:
  //  - hadError: erro no payload (throttle/THROTTLED, "temporarily unavailable" etc.)
  //  - gotDeferred: o payload de entrega chegou (chunk @defer ou payload direto). Se NÃO
  //    chegou e não há opção, foi stream incompleto/cortado -> transitório, não "sem cobertura".
  // O boundary vem do Content-Type (fallback "graphql") p/ não acoplar ao literal.
  function parseRates(body, boundary) {
    var opts = [];
    var hadError = false;
    var gotDeferred = false;
    var sep = '--' + (boundary || 'graphql');
    function collect(dg) {
      if (!dg) return;
      gotDeferred = true; // recebemos o payload de entrega (deferido ou direto)
      (dg.nodes || []).forEach(function (g) {
        (g.deliveryOptions || []).forEach(function (o) {
          // A chave composta impede associar o prazo à transportadora errada se
          // stable/unstable devolverem opções em ordens diferentes.
          opts.push(Object.assign({ _ohliGroupId: g.id || '' }, o));
        });
      });
    }
    body.split(sep).forEach(function (part) {
      var i = part.indexOf('{');
      if (i < 0) return;
      var json;
      try {
        json = JSON.parse(part.slice(i).trim());
      } catch (e) {
        return;
      }
      if (json.errors) hadError = true;
      (json.incremental || []).forEach(function (inc) {
        if (inc.errors) hadError = true;
        collect((inc.data || {}).deliveryGroups);
      });
      collect(json.data && json.data.cart && json.data.cart.deliveryGroups);
    });
    return { opts: opts, hadError: hadError, gotDeferred: gotDeferred };
  }

  function requestRates(root, cartId, query, apiVersion, signal) {
    return gql(
      root,
      { query: query, variables: { id: cartId } },
      'multipart/mixed',
      signal,
      apiVersion
    ).then(function (r) {
      var ct = (r.headers && r.headers.get('content-type')) || '';
      var m = ct.match(/boundary=([^;]+)/i);
      var boundary = m ? m[1].trim().replace(/^"|"$/g, '') : 'graphql';
      return r.text().then(function (body) {
        return parseRates(body, boundary);
      });
    });
  }

  function optionKey(o) {
    if (!o || !o._ohliGroupId || !o.handle) return '';
    return o._ohliGroupId + '|' + o.handle;
  }

  // deliveryPromise é texto de terceiro. Strings fora do formato curto esperado
  // são omitidas: preço e disponibilidade nunca dependem desse enriquecimento.
  function cleanDeliveryPromise(value) {
    var text = String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim();
    return text && text.length <= 80 ? text : '';
  }

  function mergeDeliveryPromises(stableOpts, promiseOpts) {
    var counts = {};
    var promises = {};
    (promiseOpts || []).forEach(function (o) {
      var key = optionKey(o);
      var value = cleanDeliveryPromise(o.deliveryPromise);
      if (!key || !value) return;
      counts[key] = (counts[key] || 0) + 1;
      promises[key] = value;
    });
    return (stableOpts || []).map(function (o) {
      var copy = Object.assign({}, o);
      var key = optionKey(o);
      if (key && counts[key] === 1) copy.deliveryPromise = promises[key];
      return copy;
    });
  }

  function usableExperimentalRates(parsed) {
    return !!(parsed && !parsed.hadError && parsed.gotDeferred);
  }

  function calculationCurrent(root, calcId, variantId, digits) {
    var input = root.querySelector('[data-ohli-frete-cep]');
    var currentDigits = ((input && input.value) || '').replace(/\D/g, '');
    return root._ohliCalcId === calcId && currentVariantGID(root) === variantId && currentDigits === digits;
  }

  /* --- GA4 -----------------------------------------------------------------
   * Publica shipping_simulated (ver assets/ohli-analytics.js -> custom pixel).
   * `result` é o que interessa medir — simular frete e não ver opção (ou ver
   * caro) é uma das saídas suspeitas do funil da PDP.
   *
   * `zip` vai COMPLETO (decisão do usuário, 26/jul/2026; antes ia truncado em 3
   * dígitos). Duas consequências a lembrar: (1) CEP de 8 dígitos identifica
   * quarteirão — o pixel roda hoje sem banner de consentimento, então isso entra
   * na conta no dia em que a loja montar o gate de LGPD; (2) cardinalidade de
   * milhões — NÃO registrar como dimensão personalizada no GA4 (estouraria o teto
   * e o excedente cairia em "(other)"); analisar pelo BigQuery.
   * ------------------------------------------------------------------------ */
  // Título da variante ("M / Preto") a partir das opções marcadas no form —
  // MESMO vocabulário que a integração nativa usa em item_variant. Mandar o id
  // aqui misturaria dois vocabulários na dimensão e estragaria o breakdown.
  // O <variant-selects> é IRMÃO do form de add-to-cart, não filho -> procurar a
  // partir do <product-info> (e não do form) senão não acha nada. Cobre os dois
  // modos do Dawn: radios (swatch/pill) e <select>.
  function currentVariantTitle(root) {
    var escopo = (root.closest && root.closest('product-info')) || document;
    var picker = escopo.querySelector('variant-selects');
    if (!picker) return undefined;
    var vals = [];
    var marcados = picker.querySelectorAll('fieldset input:checked');
    for (var i = 0; i < marcados.length; i++) vals.push(marcados[i].value);
    if (!vals.length) {
      var selects = picker.querySelectorAll('select');
      for (var j = 0; j < selects.length; j++) vals.push(selects[j].value);
    }
    return vals.length ? vals.join(' / ') : undefined;
  }

  function trackFrete(root, digits, result, extras) {
    if (typeof window.OhliTrack !== 'function') return;
    var payload = {
      result: result,
      // MASCARADO ("01310-100") de propósito: o GA4 coage string numérica para
      // inteiro e comeria o zero à esquerda — "01310100" chegou como 1310100 no
      // BigQuery, o que quebra toda a capital de SP e mais. O hífen segura o tipo.
      // Só quando completo: `zip` é o CEP que de fato foi cotado.
      zip: digits.length === 8 ? maskCEP(digits) : undefined,
      // O que a cliente digitou, SEMPRE — válido ou não. Campo separado de `zip`
      // de propósito: parcial misturado ali estragaria o breakdown geográfico.
      // Serve para auditar a validação custom (`invalid_zip` é só contagem de
      // dígitos) e responder "estamos rejeitando CEP que existe?".
      // O hífen à direita é o mesmo truque anti-coerção acima: maskCEP só insere
      // hífen a partir de 6 dígitos, então "01310" viraria o int 1310 sem ele.
      zip_input: maskCEP(digits) + (digits.length > 5 ? '' : '-'),
      item_id: root.dataset.productId || undefined,
      item_name: root.dataset.productTitle || undefined,
      item_brand: root.dataset.productVendor || undefined,
      item_category: root.dataset.productType || undefined,
      item_variant: currentVariantTitle(root),
      variant_id: (currentVariantGID(root).match(/\d+$/) || [])[0],
    };
    Object.keys(extras || {}).forEach(function (k) {
      if (extras[k] !== undefined) payload[k] = extras[k];
    });
    // erro de tracking NUNCA pode quebrar a calculadora
    try {
      window.OhliTrack('shipping_simulated', payload);
    } catch (e) {
      /* silêncio proposital */
    }
  }

  // As duas saídas de validação retornam ANTES do _ohliBusy, então não têm o
  // throttle natural da requisição em voo: segurar Enter com CEP incompleto
  // publicaria um evento por repetição do teclado e a distribuição de `result`
  // encheria de invalid_zip. Campo vazio não é simulação nenhuma -> nem conta.
  var VALIDACAO_MS = 1500;
  function trackValidacao(root, digits, result) {
    if (!digits) return;
    var agora = Date.now();
    if (root._lastValid && agora - root._lastValid < VALIDACAO_MS) return;
    root._lastValid = agora;
    trackFrete(root, digits, result);
  }

  function setStatus(results, html, role) {
    results.setAttribute('role', role || 'status');
    results.innerHTML = html;
  }

  function renderOptions(results, opts, geo) {
    if (!opts.length) {
      setStatus(results, '<p class="ohli-frete__msg">Sem opções de entrega para este CEP.</p>');
      return;
    }
    var head =
      '<p class="ohli-frete__dest">Entrega para <strong>' +
      esc(geo.localidade) +
      '</strong> · ' +
      esc(geo.uf) +
      '</p>';
    var root = results.closest('[data-ohli-frete]');
    var form = root && document.getElementById(root.dataset.formId);
    var preorder = form && form.querySelector('[data-ohli-preorder-date]');
    var preorderMessage = preorder && !preorder.disabled ? preorder.value : '';
    var promisesCount = opts.filter(function (o) {
      return !preorderMessage && cleanDeliveryPromise(o.deliveryPromise);
    }).length;
    var rows = opts
      .map(function (o) {
        // deliveryPromise pode ser uma data absoluta calculada para postagem
        // imediata. Não a reaproveitar como prazo de uma pré-venda.
        var promise = preorderMessage ? '' : cleanDeliveryPromise(o.deliveryPromise);
        var promiseHtml = promise
          ? '<span class="ohli-frete__opt-promise">Prazo estimado: ' + esc(promise) + '</span>'
          : promisesCount
            ? '<span class="ohli-frete__opt-promise">Prazo confirmado no checkout</span>'
            : '';
        return (
          '<li class="ohli-frete__opt">' +
          '<span class="ohli-frete__opt-details">' +
          '<span class="ohli-frete__opt-name">' +
          esc(cleanTitle(o.title)) +
          '</span>' +
          promiseHtml +
          '</span>' +
          '<span class="ohli-frete__opt-price">' +
          esc(fmtBRL(o.estimatedCost && o.estimatedCost.amount)) +
          '</span>' +
          '</li>'
        );
      })
      .join('');
    setStatus(
      results,
      head +
        '<ul class="ohli-frete__list">' +
        rows +
        '</ul>' +
        '<p class="ohli-frete__note">' +
        (preorderMessage ? 'Pré-venda: ' + esc(preorderMessage) : promisesCount
          ? 'Prazos e valores confirmados no checkout antes do pagamento.'
          : 'Prazo de entrega calculado no checkout antes do pagamento.') +
        '</p>'
    );
  }

  function renderError(results, msg) {
    // role="alert" -> leitor de tela anuncia o erro imediatamente
    setStatus(results, '<p class="ohli-frete__msg ohli-frete__msg--err">' + esc(msg) + '</p>', 'alert');
  }

  function setBtnLoading(btn, loading) {
    if (loading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.dataset.label = btn.dataset.label || btn.textContent.trim();
      btn.setAttribute('aria-label', 'Calculando frete');
      btn.innerHTML = '<span class="ohli-frete__spinner" aria-hidden="true"></span>';
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-label');
      btn.textContent = btn.dataset.label || 'Calcular';
    }
  }

  function calcular(root) {
    var results = root.querySelector('[data-ohli-frete-results]');
    var cepInput = root.querySelector('[data-ohli-frete-cep]');
    var btn = root.querySelector('[data-ohli-frete-submit]');

    if (root._ohliBusy) return; // trava reentrância (clique/Enter repetidos)

    var digits = (cepInput.value || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      renderError(results, 'Digite um CEP válido (8 dígitos).');
      trackValidacao(root, digits, 'invalid_zip');
      return;
    }
    if (!variantAvailable(root)) {
      renderError(results, 'Selecione uma variante disponível para calcular o frete.');
      trackValidacao(root, digits, 'variant_unavailable');
      return;
    }

    root._ohliBusy = true;
    var calcId = (root._ohliCalcId || 0) + 1;
    root._ohliCalcId = calcId;
    // snapshot da variante cotada -> invalida o resultado se a cliente trocar depois.
    var calcVariant = currentVariantGID(root);
    setStatus(results, '<p class="ohli-frete__msg ohli-frete__msg--loading">Calculando frete…</p>');
    setBtnLoading(btn, true);

    // deadline GLOBAL: um único controller p/ toda a cadeia -> pior caso ~TIMEOUT_MS
    // (em vez de ~3×TIMEOUT_MS somando as etapas sequenciais).
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var deadline = ctrl
      ? setTimeout(function () {
          ctrl.abort();
        }, TIMEOUT_MS)
      : null;
    var sig = ctrl ? ctrl.signal : undefined;
    var enrichmentPromise = null;
    // acumula o desfecho ao longo da cadeia; emitido uma única vez no finally
    var track = { result: 'error', extras: {} };

    // ViaCEP (CEP->UF/cidade) e o carrinho descartável em paralelo. O cartCreate
    // normalmente já foi disparado no input do campo -> aqui só é aguardado (~0ms).
    Promise.all([
      fetchTimeout('https://viacep.com.br/ws/' + digits + '/json/', { signal: sig }).then(function (r) {
        return r.json();
      }),
      ensureCart(root),
    ])
      .then(function (arr) {
        var geo = arr[0];
        var cartId = arr[1];
        if (!geo || geo.erro) {
          renderError(results, 'CEP não encontrado.');
          track.result = 'zip_not_found';
          return null;
        }
        track.extras.state = geo.uf;
        // carrinho é one-shot: consumido aqui, o próximo cálculo cria/pré-cria outro.
        root._ohliCart = null;
        return gql(
          root,
          {
            query: Q_ADDR,
            variables: {
              id: cartId,
              a: {
                address: {
                  deliveryAddress: {
                    countryCode: 'BR',
                    zip: maskCEP(digits),
                    provinceCode: geo.uf,
                    city: geo.localidade,
                  },
                },
                selected: true,
                validationStrategy: 'COUNTRY_CODE_ONLY',
              },
            },
          },
          null,
          sig
        )
          .then(function (r) {
            return r.json();
          })
          .then(function (addrRes) {
            var add = addrRes && addrRes.data && addrRes.data.cartDeliveryAddressesAdd;
            var ue = (add && add.userErrors) || (addrRes && addrRes.errors);
            if (ue && ue.length) throw new Error('addr'); // endereço rejeitado -> transitório
            // Preço/opções vêm SEMPRE da estável. A unstable roda em paralelo e
            // só acrescenta deliveryPromise; falha experimental nunca segura o
            // spinner nem apaga uma tarifa válida.
            var stableOpts = null;
            var promiseParsed = null;
            enrichmentPromise = requestRates(root, cartId, Q_RATES_PROMISE, 'unstable', sig)
              .then(function (parsed) {
                // Nunca usa um stream experimental parcial: GraphQL pode devolver
                // dados E errors no mesmo payload.
                if (!usableExperimentalRates(parsed)) return null;
                promiseParsed = parsed;
                if (
                  stableOpts &&
                  parsed.opts.length &&
                  calculationCurrent(root, calcId, calcVariant, digits)
                ) {
                  renderOptions(results, mergeDeliveryPromises(stableOpts, parsed.opts), geo);
                }
                return parsed;
              })
              .catch(function () {
                return null;
              });

            return requestRates(root, cartId, Q_RATES, root.dataset.apiVersion, sig)
              .catch(function () {
                return { opts: [], hadError: true, gotDeferred: false };
              })
              .then(function (parsed) {
                if (!calculationCurrent(root, calcId, calcVariant, digits)) return;
                if (parsed.opts.length) {
                  stableOpts = parsed.opts;
                  renderOptions(
                    results,
                    promiseParsed && promiseParsed.opts.length
                      ? mergeDeliveryPromises(stableOpts, promiseParsed.opts)
                      : stableOpts,
                    geo
                  );
                  var valores = stableOpts
                    .map(function (o) {
                      return parseFloat(o.estimatedCost && o.estimatedCost.amount);
                    })
                    .filter(function (n) {
                      return !isNaN(n);
                    });
                  track.result = 'ok';
                  track.extras.options_count = stableOpts.length;
                  if (valores.length) {
                    track.extras.shipping_min = Math.min.apply(null, valores);
                    track.extras.shipping_max = Math.max.apply(null, valores);
                    track.extras.free_shipping = track.extras.shipping_min === 0 ? 1 : 0;
                  }
                  return;
                }
                if (!parsed.hadError && parsed.gotDeferred) {
                  renderOptions(results, [], geo); // sem cobertura genuína na fonte estável
                  track.result = 'no_coverage';
                  return;
                }

                // Só se a fonte estável estiver inválida usamos a unstable como
                // fallback de preço. Ela já está em voo, sem somar outra latência.
                return enrichmentPromise.then(function (fallback) {
                  if (!calculationCurrent(root, calcId, calcVariant, digits)) return;
                  if (usableExperimentalRates(fallback) && fallback.opts.length) {
                    renderOptions(results, fallback.opts, geo);
                    var fallbackValues = fallback.opts
                      .map(function (o) {
                        return parseFloat(o.estimatedCost && o.estimatedCost.amount);
                      })
                      .filter(function (n) {
                        return !isNaN(n);
                      });
                    track.result = 'ok';
                    track.extras.options_count = fallback.opts.length;
                    if (fallbackValues.length) {
                      track.extras.shipping_min = Math.min.apply(null, fallbackValues);
                      track.extras.shipping_max = Math.max.apply(null, fallbackValues);
                      track.extras.free_shipping = track.extras.shipping_min === 0 ? 1 : 0;
                    }
                  } else if (usableExperimentalRates(fallback)) {
                    renderOptions(results, [], geo);
                    track.result = 'no_coverage';
                  } else {
                    renderError(results, RETRY_MSG);
                  }
                });
              });
          });
      })
      .catch(function () {
        if (calculationCurrent(root, calcId, calcVariant, digits)) renderError(results, RETRY_MSG);
        track.result = 'error';
      })
      .finally(function () {
        // Se a estável terminou primeiro, mantém o deadline apenas para abortar
        // o enriquecimento em background. A interface já está liberada.
        if (deadline) {
          if (enrichmentPromise) {
            enrichmentPromise.finally(function () {
              clearTimeout(deadline);
            });
          } else {
            clearTimeout(deadline);
          }
        }
        root._ohliBusy = false;
        // marca o que foi cotado só se variante E CEP continuam os mesmos.
        var emitir = calculationCurrent(root, calcId, calcVariant, digits);
        if (emitir) {
          root._quoted = { v: calcVariant, c: digits };
        } else {
          // cotação descartada (trocou de variante no meio) -> não vira evento
          root._quoted = null;
          setStatus(results, '');
        }
        setBtnLoading(btn, false);
        // depois de restaurar o botão: um throw aqui deixaria a calculadora
        // travada com o spinner para sempre.
        if (emitir) trackFrete(root, digits, track.result, track.extras);
      });
  }

  function init(root) {
    if (root.dataset.ohliFreteReady) return;
    root.dataset.ohliFreteReady = '1';
    var cepInput = root.querySelector('[data-ohli-frete-cep]');
    var btn = root.querySelector('[data-ohli-frete-submit]');
    var results = root.querySelector('[data-ohli-frete-results]');
    cepInput.addEventListener('input', function () {
      cepInput.value = maskCEP(cepInput.value);
      var digits = cepInput.value.replace(/\D/g, '');
      // se a cliente começa a digitar um CEP diferente do cotado, limpa o resultado antigo.
      if (root._quoted && root._quoted.c && digits !== root._quoted.c) {
        root._quoted = null;
        setStatus(results, '');
      }
      // pré-cria o carrinho descartável com CEP parcial (>=5 dígitos -> menos carts órfãos).
      if (digits.length >= 5) ensureCart(root).catch(function () {});
    });
    cepInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.repeat) {
        e.preventDefault();
        calcular(root);
      }
    });
    btn.addEventListener('click', function () {
      calcular(root);
    });
  }

  function boot() {
    document.querySelectorAll('[data-ohli-frete]').forEach(init);
  }

  // Um ÚNICO listener no módulo (não por-widget) invalida o resultado exibido quando a
  // cliente troca de variante depois de calcular (o frete pode mudar por peso). Itera só
  // widgets ainda no DOM, então não segura referências de nós destacados.
  document.addEventListener('change', function () {
    document.querySelectorAll('[data-ohli-frete]').forEach(function (root) {
      if (!root._quoted) return;
      if (currentVariantGID(root) !== root._quoted.v) {
        root._quoted = null;
        root._ohliCart = null;
        var results = root.querySelector('[data-ohli-frete-results]');
        if (results) setStatus(results, '');
      }
    });
  });

  // Re-inicializa se o Dawn re-renderiza a seção (combined listings / troca de produto
  // substitui o <product-info> inteiro -> widget novo sem listeners).
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (
            n.nodeType === 1 &&
            ((n.matches && n.matches('[data-ohli-frete]')) ||
              (n.querySelector && n.querySelector('[data-ohli-frete]')))
          ) {
            boot();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
